import { Link, useParams } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { MetricsContent } from "@/components/v2/metrics/metrics-content";
import { MetricsNotPublished } from "@/components/v2/metrics-not-published";
import { MetricsNotPublishedActions } from "@/components/v2/metrics-not-published-actions";
import { QueryErrorState } from "@/components/v2/query-error-state";
import { useRdMetrics } from "@/lib/metrics";
import type { FunctionalFormLink } from "@/lib/metrics-display";
import { canRenameRd } from "@/lib/permissions";
import {
  SurfaceShell,
  SurfaceStatus,
  surfaceHeader,
  useSurfaceBase,
} from "./rd-workspace";

/**
 * Metrics — every provisional catalog metric with history for one RD. The RD
 * detail gates the page exactly like every other surface (403/404 treatment
 * in SurfaceStatus); the metrics hook's own lifecycle renders inline below the
 * header strip, so a flag that is off never redirects.
 */
export function MetricsSurface(): ReactElement {
  const { rdId } = useParams({ strict: false }) as { rdId: string };
  const { rd, me } = useSurfaceBase(rdId);
  const detail = rd.data?.data;
  if (!detail) return <SurfaceStatus rd={rd} />;

  return (
    <SurfaceShell detail={detail} surfaceLabel="Metrics">
      {surfaceHeader(rdId, detail, me.data ? canRenameRd(me.data.user) : false)}
      <MetricsBody rdId={detail.id} slug={rdId} name={detail.name} />
    </SurfaceShell>
  );
}

/** The missing-minimum flag's link to this RD's Functional form (NO-667). */
function functionalFormLinkFor(rdSlug: string): FunctionalFormLink {
  return function FunctionalFormLinkTo(label) {
    return (
      <Link
        to="/rds/$rdId/forms"
        params={{ rdId: rdSlug }}
        className="font-medium text-primary underline-offset-4 hover:underline"
      >
        {label}
      </Link>
    );
  };
}

function MetricsBody({
  rdId,
  slug,
  name,
}: {
  rdId: string;
  slug: string;
  name: string;
}): ReactElement {
  const state = useRdMetrics(rdId, { detail: true });

  if (state.status === "loading") {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (state.status === "disabled") {
    return (
      <p className="rounded-xl border border-border bg-background px-[14px] py-3 text-sm text-muted-strong">
        Metrics aren&apos;t turned on for this workspace yet.
      </p>
    );
  }

  if (state.status === "not_published") {
    return (
      <MetricsNotPublished
        name={name}
        detail
        actions={<MetricsNotPublishedActions rdId={slug} />}
      />
    );
  }

  if (state.status === "error") {
    return (
      <QueryErrorState
        error={state.error.cause}
        notFoundText="Metrics aren't available for this RD"
        onRetry={() => state.retry?.()}
        className="py-16"
      />
    );
  }

  return (
    <MetricsContent
      key={state.data.rdId}
      data={state.data}
      functionalFormLink={functionalFormLinkFor(slug)}
    />
  );
}
